import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { authService } from './services/api';
import { QAProcessingProvider } from './contexts/QAProcessingContext';
import { RerunProvider } from './contexts/RerunContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Sidebar from './components/layout/Sidebar';
import QAProcessingIndicator from './components/QAProcessingIndicator';
import LoginView from './views/LoginView';

// Lazy-loaded route components for code splitting
const DashboardView = lazy(() => import('./views/DashboardView'));
const ProjectsView = lazy(() => import('./views/ProjectsView'));
const ProjectDetailView = lazy(() => import('./views/ProjectDetailView'));
const ResultsView = lazy(() => import('./views/ResultsView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const AdminPanelView = lazy(() => import('./views/AdminPanelView'));

// Loading fallback for lazy-loaded routes
const RouteLoader = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 className="w-8 h-8 text-[#4F7DF3] animate-spin" />
  </div>
);

// ============================================================
// ROUTE WRAPPERS
// ============================================================

const ProjectDetailWrapper = ({ onMenuClick }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/projects');
  };

  return (
    <Suspense fallback={<RouteLoader />}>
      <ProjectDetailView projectId={projectId} onBack={handleBack} onMenuClick={onMenuClick} />
    </Suspense>
  );
};

const DashboardWrapper = ({ onMenuClick }) => {
  const navigate = useNavigate();

  const handleViewChange = (view, state) => {
    navigate(`/${view}`, state ? { state } : undefined);
  };

  return (
    <Suspense fallback={<RouteLoader />}>
      <DashboardView setCurrentView={handleViewChange} onMenuClick={onMenuClick} />
    </Suspense>
  );
};

const ProjectsWrapper = ({ onMenuClick, currentUser }) => {
  const navigate = useNavigate();

  const handleSelectProject = (project) => {
    navigate(`/projects/${project.id}`);
  };

  return (
    <Suspense fallback={<RouteLoader />}>
      <ProjectsView onSelectProject={handleSelectProject} onMenuClick={onMenuClick} currentUser={currentUser} />
    </Suspense>
  );
};

const ResultsWrapper = ({ onMenuClick }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const location = useLocation();

  const expandProjectId = location.state?.expandProjectId || null;
  const expandPageId = location.state?.expandPageId || null;

  const handleSelectProjectForResults = (project) => {
    setSelectedProject(project);
  };

  return (
    <Suspense fallback={<RouteLoader />}>
      <ResultsView project={selectedProject} onSelectProject={handleSelectProjectForResults} onMenuClick={onMenuClick} expandProjectId={expandProjectId} expandPageId={expandPageId} />
    </Suspense>
  );
};

// ============================================================
// MAIN APP
// ============================================================

export default function App() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { user } = await authService.getSession();
        setCurrentUser(user);
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    checkSession();
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const onMenuClick = () => setSidebarOpen(true);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-qa-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-qa-primary animate-spin" />
      </div>
    );
  }

  if (!currentUser) {
    return <LoginView onLogin={handleLogin} />;
  }

  const handleNavigateToProject = (projectId) => {
    navigate(`/projects/${projectId}`);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <ThemeProvider>
      <QAProcessingProvider>
        <RerunProvider>
          <div className="min-h-screen bg-qa-bg text-qa-text-primary transition-colors duration-300">
            {/* Skip to content link for accessibility */}
            <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-[#4F7DF3] focus:text-white focus:rounded-lg">
              Skip to content
            </a>

            <Sidebar
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
              currentUser={currentUser}
            />

            <main id="main-content" className="lg:pl-64 transition-all duration-300">
              <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardWrapper onMenuClick={onMenuClick} />} />
                  <Route path="/projects" element={<ProjectsWrapper onMenuClick={onMenuClick} currentUser={currentUser} />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailWrapper onMenuClick={onMenuClick} />} />
                  <Route path="/results" element={<ResultsWrapper onMenuClick={onMenuClick} />} />
                  <Route path="/settings" element={
                    <Suspense fallback={<RouteLoader />}>
                      <SettingsView onMenuClick={onMenuClick} />
                    </Suspense>
                  } />
                  <Route
                    path="/admin"
                    element={
                      currentUser?.role === 'admin'
                        ? <Suspense fallback={<RouteLoader />}>
                          <AdminPanelView currentUser={currentUser} onMenuClick={onMenuClick} />
                        </Suspense>
                        : <Navigate to="/dashboard" replace />
                    }
                  />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </main>

            {/* Global QA Processing Indicator */}
            <QAProcessingIndicator onNavigateToProject={handleNavigateToProject} />

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'var(--color-card)',
                  color: 'var(--color-text-primary)',
                  border: '1px solid var(--color-card-border)',
                  borderRadius: '10px',
                },
                success: {
                  iconTheme: { primary: 'var(--color-success)', secondary: 'var(--color-card)' },
                },
                error: {
                  iconTheme: { primary: 'var(--color-error)', secondary: 'var(--color-card)' },
                },
              }}
            />
          </div>
        </RerunProvider>
      </QAProcessingProvider>
    </ThemeProvider>
  );
}
